// Convergencia builder ↔ reports.detailed (FUENTE ÚNICA).
// - builderToDetailed: estado del builder → { pages, sections } con las claves REALES que
//   consume el generador (getConfigPage / mapSection) + se conserva el `settings` del
//   builder dentro de cada entrada (round-trip sin pérdida; el backend ignora las claves
//   extra). groupcharts/exec se guardan como páginas con código sintético (el backend
//   ignora códigos desconocidos).
// - detailedToBuilder: reconstruye el estado del builder. Si la entrada trae `settings`
//   del builder (origen builder) se usa tal cual (lossless); si no (config del tab viejo),
//   se hace un reverse-map desde las claves reales.
import { discName, TYPES } from '../data/page-builder.render'
import { templatePages } from '../data/report-templates.data'

// Páginas base que un informe debería tener por defecto (mismo set inicial del Constructor).
const BASE_PAGE_ORDER = ['cover', 'dashboard', 'groupcharts', 'tasktable']

// Garantiza las páginas base conservando el ORDEN DE APARICIÓN original: las páginas
// existentes no se mueven; solo se INSERTAN las base que falten, cada una tras su
// predecesor base canónico que ya exista (o al frente si no hay ninguno). Se usa al traer
// la config de otro proyecto (los antiguos solo traían la portada).
export function ensureBasePages(pages) {
  const list = Array.isArray(pages) ? pages.slice() : []
  BASE_PAGE_ORDER.forEach((type, idx) => {
    if (list.some((p) => p.type === type)) return
    let insertAt = 0
    for (let k = idx - 1; k >= 0; k--) {
      const pi = list.findIndex((p) => p.type === BASE_PAGE_ORDER[k])
      if (pi >= 0) { insertAt = pi + 1; break }
    }
    list.splice(insertAt, 0, { type, visible: true, settings: TYPES[type].defaults() })
  })
  return list
}

const IMG_SRC_TO_DOC = { activity: 'activity_document', plan: 'drawing', none: null }
const DOC_TO_IMG = { activity_document: 'activity', drawing: 'plan' } // null/'' → 'none'
// LINE_SELECTOR (página real por defecto) = Gráficos por grupo; Curva S es nueva (SCURVE).
const CODE_BY_TYPE = { cover: 'REPORT_COVER', dashboard: 'RAG_SUMMARY', groupcharts: 'LINE_SELECTOR', tasktable: 'TASK_COLUMNS_SELECTOR', scurve: 'SCURVE', exec: 'EXEC' }
const TYPE_BY_CODE = Object.keys(CODE_BY_TYPE).reduce((a, t) => { a[CODE_BY_TYPE[t]] = t; return a }, {})

// ---------------- Auto-enlace disciplina↔grupo ----------------
// Heurística por nombre (ES/EN), normalizado sin acentos. `set` con límite de palabra.
const HEUR = {
  OC: /civil|obra/,
  MM: /mecan|mechanic|estructura|structure|modul|hinca|piling/,
  ME: /electr|cableado|wiring/,
  LEV: /evacua|evacuation/,
  SET: /subest|substation|s\/e|\bset\b/,
  BESS: /bess|bateria|battery|storage/,
}
function norm(s) { return (s == null ? '' : String(s)).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '') }

// Devuelve { groupId, source } o null. flatGroups: [{ id, name, code_custom }]. usedIds: ids ya tomados.
// mode: 'exact' (solo code_custom) | 'heuristic' (solo nombre) | 'both'.
export function matchGroupForDiscipline(code, flatGroups, usedIds, mode) {
  const C = String(code || '').toUpperCase()
  const free = (g) => usedIds.indexOf(g.id) < 0
  if (mode !== 'heuristic') {
    const exact = (flatGroups || []).find((g) => free(g) && String(g.code_custom || '').toUpperCase() === C)
    if (exact) return { groupId: exact.id, source: 'code_custom' }
    if (mode === 'exact') return null
  }
  const re = HEUR[C]
  if (re) {
    const g = (flatGroups || []).find((x) => free(x) && re.test(norm(x.name)))
    if (g) return { groupId: g.id, source: 'heuristic' }
  }
  return null
}

// ---------------- builder → detailed ----------------
export function builderToDetailed(builderJson) {
  const pagesIn = (builderJson && builderJson.pages) || []
  const pages = []
  const sections = []

  pagesIn.forEach((p, idx) => {
    const s = p.settings || {}
    const visible = p.visible !== false
    const order = idx
    // SLIM: solo lo dinámico (code/visible/order/settings). El catálogo estático
    // (name/type/customizationType/isUserManaged/isEditable/description/builderType) NO se
    // persiste; se rehidrata por `code` al cargar (detailedToBuilder) y los consumidores
    // (getConfigPage, render.js) solo necesitan code/visible/settings.
    const pageBase = { visible, order }

    switch (p.type) {
      case 'cover':
        pages.push({ ...pageBase, code: 'REPORT_COVER', settings: { ...s, title: s.title ?? null, subtitle: s.subtitle ?? null, description: s.description ?? null, image_cover: s.image_cover || { type: 'file', name: null, mimeType: null, url: null, file: null } } })
        break
      case 'dashboard':
        pages.push({ ...pageBase, code: 'RAG_SUMMARY', settings: { ...s, showGroupProgress: s.showGroupProgress ?? 'parent', customGroups: s.customGroups || [], firstDatePairType: s.firstDatePairType ?? 'scheduled', secondDatePairType: s.secondDatePairType ?? 'current', showEndDateOnCompletion: !!s.showEndDateOnCompletion } })
        break
      case 'groupcharts':
        // Gráficos por grupo = LINE_SELECTOR real (líneas baseLine/lastPlanned/actual).
        pages.push({ ...pageBase, code: 'LINE_SELECTOR', settings: { ...s, baseLine: s.scheduled !== false, lastPlanned: !!s.lastPlanned, actual: s.current !== false } })
        break
      case 'tasktable':
        // actualProgress = columna "Current Progress (unit)" en la Blade → la controla el
        // toggle currentUnit del builder (antes estaba fijo en true).
        pages.push({ ...pageBase, code: 'TASK_COLUMNS_SELECTOR', settings: { ...s, baseLine: s.schedule !== false, lastPlanned: !!s.latest, actual: s.actual !== false, baseLineProgress: s.scheduledPct !== false, lastPlannedProgress: !!s.latestPct, actualProgress: s.currentUnit !== false } })
        break
      case 'scurve':
        // Curva S: tipo NUEVO sin equivalente real (código sintético; backend lo ignora).
        pages.push({ ...pageBase, code: 'SCURVE', settings: { ...s } })
        break
      case 'exec':
        pages.push({ ...pageBase, code: 'EXEC', settings: { ...s } })
        break

      // Secciones: se conserva customizationType ('user-editable') porque el form de
      // generación filtra por él, y name/hasAssociatedGroup/flags que ese form consume.
      case 'discipline':
        sections.push({ code: s.discipline, name: s.name || discName(s.discipline), visible, order, hasAssociatedGroup: true, canDisableSection: !!s.userCanHide, numberOfExtraImages: s.numberOfExtraImages || 0, groupId: s.groupId ?? null, documentType: IMG_SRC_TO_DOC[s.imageSource] ?? null, userCanDefineLegend: !!s.userCanDefineLegend, ganttLandscape: false, ganttUserCanChangeOrientation: false, customizationType: 'user-editable', settings: { ...s } })
        break
      case 'images':
        sections.push({ code: sections.some((x) => x.code === 'GENERAL') ? ('GENERAL_' + idx) : 'GENERAL', name: 'GENERAL', visible, order, hasAssociatedGroup: false, canDisableSection: false, numberOfExtraImages: s.numberOfExtraImages || 0, groupId: null, documentType: null, userCanDefineLegend: !!s.userCanDefineLegend, ganttLandscape: false, ganttUserCanChangeOrientation: false, customizationType: 'user-editable', settings: { ...s } })
        break
      case 'gantt':
        sections.push({ code: 'GANTT', name: 'GANTT', visible, order, hasAssociatedGroup: false, canDisableSection: !!s.userCanHide, numberOfExtraImages: Math.max(0, s.numberOfImages || 1), groupId: null, documentType: null, userCanDefineLegend: !!s.showColorLegend, ganttLandscape: !!s.landscape, ganttUserCanChangeOrientation: !!s.userCanChangeOrientation, customizationType: 'user-editable', settings: { ...s } })
        break
      default:
        break
    }
  })

  const out = { pages, sections }
  if (builderJson && builderJson.templateKey) out.templateKey = builderJson.templateKey
  // Proyecto de origen cuando la config se copió de otro proyecto (para mostrar "En uso · copiado de").
  if (builderJson && builderJson.importedFrom) out.importedFrom = builderJson.importedFrom
  return out
}

// Mappings disciplina↔grupo para assign-code-custom: [{ id: groupId, code_custom: code }].
export function disciplineGroupMappings(builderJson) {
  const pagesIn = (builderJson && builderJson.pages) || []
  return pagesIn
    .filter((p) => p.type === 'discipline' && p.settings && p.settings.groupId)
    .map((p) => ({ id: p.settings.groupId, code_custom: p.settings.discipline }))
}

// ---------------- detailed → builder ----------------
function reverseSettings(e, type) {
  const s = e.settings || {}
  switch (type) {
    case 'cover':
      return { title: s.title ?? null, subtitle: s.subtitle ?? null, description: s.description ?? null, image_cover: s.image_cover || { type: 'file', name: null, mimeType: null, url: null, file: null } }
    case 'dashboard':
      // showChart/showGeneral/showTable los introdujo el builder: en configs antiguas (p.ej.
      // Naduri, Lorca) no existen → default a true para no pintar un dashboard vacío
      // ("sin contenido"). Si un config del builder los trae en false, el stash prevalece.
      return { showGroupProgress: s.showGroupProgress ?? 'parent', customGroups: s.customGroups || [], firstDatePairType: s.firstDatePairType ?? 'scheduled', secondDatePairType: s.secondDatePairType ?? 'current', showEndDateOnCompletion: !!s.showEndDateOnCompletion, showChart: s.showChart !== false, showGeneral: s.showGeneral !== false, showTable: s.showTable !== false }
    case 'groupcharts':
      return { scheduled: s.baseLine !== false, current: s.actual !== false, lastPlanned: !!s.lastPlanned }
    case 'scurve':
      return {} // nueva; todo viene del stash del builder
    case 'tasktable':
      return { schedule: s.baseLine !== false, latest: !!s.lastPlanned, actual: s.actual !== false, currentUnit: s.actualProgress !== false, scheduledPct: s.baseLineProgress !== false, latestPct: !!s.lastPlannedProgress }
    case 'discipline':
      return { discipline: e.code, name: e.name || discName(e.code), groupId: e.groupId ?? null, numberOfExtraImages: e.numberOfExtraImages || 0, userCanHide: !!e.canDisableSection, imageSource: DOC_TO_IMG[e.documentType] || 'none', userCanDefineLegend: !!e.userCanDefineLegend }
    case 'images':
      return { numberOfExtraImages: e.numberOfExtraImages || 0, userCanDefineLegend: !!e.userCanDefineLegend }
    case 'gantt':
      return { landscape: !!e.ganttLandscape, userCanChangeOrientation: !!e.ganttUserCanChangeOrientation, userCanHide: !!e.canDisableSection, numberOfImages: e.numberOfExtraImages || 1, showColorLegend: !!e.userCanDefineLegend }
    default:
      return {}
  }
}

function sectionType(e) {
  if (e.builderType) return e.builderType
  if (e.code === 'GANTT') return 'gantt'
  if (String(e.code || '').indexOf('GENERAL') === 0) return 'images'
  return 'discipline'
}

export function detailedToBuilder(detailed) {
  const entries = []
  ;(detailed && detailed.pages ? detailed.pages : []).forEach((e) => {
    const type = TYPE_BY_CODE[e.code]
    if (type) entries.push({ order: e.order ?? 999, type, visible: e.visible !== false, stash: e.settings })
  })
  ;(detailed && detailed.sections ? detailed.sections : []).forEach((e) => {
    const type = sectionType(e)
    // Para secciones el reverse necesita los campos planos (e), y el stash builder va en e.settings.
    entries.push({ order: e.order ?? 999, type, visible: e.visible !== false, stash: e.settings, raw: e })
  })

  entries.sort((a, b) => (a.order || 0) - (b.order || 0))

  const pages = entries.map((en) => {
    const reverse = reverseSettings(en.raw || { settings: en.stash, code: null }, en.type)
    // stash (settings del builder, si vino de un guardado del builder) prevalece sobre el reverse.
    const settings = Object.assign({}, reverse, (en.stash && typeof en.stash === 'object') ? en.stash : {})
    return { type: en.type, visible: en.visible, settings }
  }).filter((p) => p.type)

  return { version: 1, pages, templateKey: (detailed && detailed.templateKey) || null, importedFrom: (detailed && detailed.importedFrom) || null }
}

// ---------------- Identificación de plantilla en uso ----------------
// Firma laxa: conjunto (ordenado) de identidades de páginas VISIBLES. Ignora orden real,
// settings finos, groupId/imágenes → sirve para saber "qué plantilla" y si está modificada.
export function pageSignature(pages) {
  return (pages || [])
    .filter((p) => p.visible !== false)
    .map((p) => p.type + (p.type === 'discipline' ? ':' + (p.settings && p.settings.discipline) : ''))
    .sort()
    .join('|')
}

// ¿Las páginas difieren de la plantilla `key`? (false si no hay key o no existe la plantilla)
export function isModifiedFrom(pages, key) {
  const base = key ? templatePages(key) : null
  if (!base) return false
  return pageSignature(pages) !== pageSignature(base)
}
